import { Box, Button, Menu, Stack, Tooltip, Typography } from "@mui/material";
import { ContentCopy, MobiledataOffOutlined } from "@mui/icons-material";
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  GridToolbarQuickFilter,
  GridToolbarProps,
} from "@mui/x-data-grid-premium";
import { useState, MouseEvent } from "react";
import RankingMenu, { RankingControls } from "./RankingMenu";
import { percentileGradient } from "../PercentileCell";

declare module "@mui/x-data-grid-premium" {
  interface ToolbarPropsOverrides {
    ranking: RankingControls;
    isMobile: boolean;
  }
}

interface CustomDataGridToolbarProps extends GridToolbarProps {
  ranking: RankingControls;
  isMobile: boolean;
}

export default function CustomDataGridToolbar(
  props: CustomDataGridToolbarProps,
) {
  const { csvOptions, excelOptions, printOptions, ranking, isMobile } = props;
  const { rankMode, view } = ranking;
  const viewSuffix = view === "def" ? " · Def" : "";
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLButtonElement>(
    null,
  );
  const openMenu = Boolean(anchorElMenu);
  const [copied, setCopied] = useState(false);
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <GridToolbarContainer
      sx={
        isMobile
          ? {
              ".MuiButton-startIcon": { mr: 0 },
              ".MuiButton-root": { minWidth: "auto", px: 0.75 },
            }
          : undefined
      }
    >
      <Tooltip title="Show/hide, reorder, rename, and add custom columns">
        <span>
          <GridToolbarColumnsButton
            slotProps={{
              button: { children: isMobile ? undefined : "Columns" },
            }}
          />
        </span>
      </Tooltip>
      <GridToolbarFilterButton />
      <Button
        onClick={(event: MouseEvent<HTMLButtonElement>) =>
          setAnchorElMenu(event.currentTarget)
        }
        startIcon={<MobiledataOffOutlined />}
        sx={isMobile ? { minWidth: "auto", px: 0.75 } : {}}
      >
        {isMobile
          ? ""
          : `${rankMode === "ranks" ? "Ranks" : rankMode === "off" ? "Ranking off" : "Percentiles"}${viewSuffix}`}
      </Button>
      <Menu
        id="ranking-menu"
        anchorEl={anchorElMenu}
        open={openMenu}
        onClose={() => setAnchorElMenu(null)}
      >
        <RankingMenu controls={ranking} />
      </Menu>
      {!isMobile && (
        <GridToolbarExport
          csvOptions={csvOptions}
          excelOptions={excelOptions}
          printOptions={printOptions}
        />
      )}
      <Tooltip title={copied ? "Copied!" : "Copy link to this view"}>
        <Button
          onClick={handleCopyLink}
          startIcon={<ContentCopy />}
          sx={isMobile ? { minWidth: "auto", px: 0.75 } : {}}
        >
          {isMobile ? "" : copied ? "Copied!" : "Share"}
        </Button>
      </Tooltip>
      <Box sx={{ flexGrow: 1 }} />
      {!isMobile && rankMode !== "off" && (
        <Stack mr={4} alignItems="center">
          <Typography variant="caption" color="textSecondary">
            {rankMode === "percentiles" ? "Percentile" : "Rank"}
            {view === "def" ? " · defense view" : ""}
          </Typography>
          <div
            className="grad-chart"
            style={{
              // Rank legends are labelled 1st -> Last, so the best end sits left.
              background: percentileGradient(
                rankMode === "ranks" ? "to left" : "to right",
              ),
            }}
          />
          <Box display="flex" justifyContent="space-between" width="100%">
            <Typography variant="caption" color="textSecondary">
              {rankMode === "percentiles" ? "0" : "1st"}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {rankMode === "percentiles" ? "100" : "Last"}
            </Typography>
          </Box>
        </Stack>
      )}
      <GridToolbarQuickFilter sx={isMobile ? { maxWidth: "140px" } : {}} />
    </GridToolbarContainer>
  );
}
